import { useState, useEffect } from 'react';
import { apiClient } from '../../../services/api/client';
import { Site, SiteCreateDto } from '../../../types/organisation';

export const useSites = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSites = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/organisation/sites');
      setSites(data);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, []);

  return { sites, loading, error, refresh: fetchSites };
};

export const useSiteActions = () => {
  const [loading, setLoading] = useState(false);

  const createSite = async (data: SiteCreateDto): Promise<Site> => {
    setLoading(true);
    try {
      return await apiClient.post('/organisation/sites', data);
    } finally {
      setLoading(false);
    }
  };

  const updateSite = async (id: number, data: SiteCreateDto): Promise<Site> => {
    setLoading(true);
    try {
      return await apiClient.put(`/organisation/sites/${id}`, data);
    } finally {
      setLoading(false);
    }
  };

  const deleteSite = async (id: number): Promise<void> => {
    setLoading(true);
    try {
      await apiClient.delete(`/organisation/sites/${id}`);
    } finally {
      setLoading(false);
    }
  };

  return { createSite, updateSite, deleteSite, loading };
};

export const useSite = (siteId: number | null) => {
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!siteId) {
      setSite(null);
      return;
    }
    setLoading(true);
    apiClient.get(`/organisation/sites/${siteId}`)
      .then((data) => setSite(data))
      .catch(() => setSite(null))
      .finally(() => setLoading(false));
  }, [siteId]);

  return { site, loading };
};